import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface ITooltipProps {
  tooltipText: string | React.ReactNode;
  children: React.ReactNode;
  distance?: 'small' | 'medium' | 'big';
  appearOnSide?: 'top' | 'left' | 'bottom';
  maxWidth?: number;
  className?: string;
}

export const Tooltip: React.FC<ITooltipProps> = ({
  children,
  tooltipText,
  distance = 'small',
  appearOnSide = 'top',
  maxWidth,
  className,
}) => {
  const containerReference = useRef<HTMLDivElement>(null);
  const [tooltipFixedPosition, setTooltipFixedPosition] = useState<React.CSSProperties | null>(null);

  const showTooltipAboveEveryAncestor = () => {
    const containerBounds = containerReference.current.getBoundingClientRect();
    const horizontalCenter = containerBounds.left + containerBounds.width / 2;
    const verticalCenter = containerBounds.top + containerBounds.height / 2;
    const fixedPositionBySide: Record<NonNullable<ITooltipProps['appearOnSide']>, React.CSSProperties> = {
      top: { left: horizontalCenter, top: containerBounds.top, transform: 'translate(-50%, -100%)' },
      bottom: { left: horizontalCenter, top: containerBounds.bottom, transform: 'translateX(-50%)' },
      left: { left: containerBounds.left, top: verticalCenter, transform: 'translate(-100%, -50%)' },
    };
    setTooltipFixedPosition(fixedPositionBySide[appearOnSide]);
  };

  useEffect(() => {
    if (!tooltipFixedPosition) return;
    const hideTooltip = () => setTooltipFixedPosition(null);
    window.addEventListener('scroll', hideTooltip, true);
    return () => window.removeEventListener('scroll', hideTooltip, true);
  }, [tooltipFixedPosition]);

  return (
    <div
      ref={containerReference}
      className={className ? `tooltip-container ${className}` : 'tooltip-container'}
      onMouseEnter={showTooltipAboveEveryAncestor}
      onMouseLeave={() => setTooltipFixedPosition(null)}>
      {tooltipFixedPosition &&
        createPortal(
          <div
            className={`tooltip-box ${appearOnSide} ${distance}`}
            style={{
              ...tooltipFixedPosition,
              position: 'fixed',
              right: 'auto',
              bottom: 'auto',
              ...(maxWidth && { maxWidth: `${maxWidth}px` }),
            }}>
            {tooltipText}
          </div>,
          document.body,
        )}
      {children}
    </div>
  );
};

export default Tooltip;
